import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, delay, distinctUntilChanged, map, Observable, of, Subscription, switchMap, from, catchError, EMPTY } from 'rxjs';

export class TriggeredTableDataSource<T> extends MatTableDataSource<T> {
  private isLoading = new BehaviorSubject<boolean>(false);
  private refreshTrigger: Subscription;

  isRefreshEnabled = false;

  get isLoading$(): Observable<boolean> {
    return this.delayFalse(this.isLoading.asObservable(), 100);
  }

  get isEmpty$(): Observable<boolean> {
    return this.isLoading$.pipe(
      distinctUntilChanged(),
      map(() => this.data.length === 0),
    );
  }

  constructor(private dataLoader: (...args: any[]) => Promise<T[]>) {
    super();
  }

  setRefreshTrigger(trigger: Observable<any>, isRefreshEnabled = false): void {
    this.refreshTrigger?.unsubscribe();
    this.refreshTrigger = null;
    if (trigger) {
      this.refreshTrigger = trigger
        .pipe(
          switchMap(() => from(this.refreshData())),
          catchError(error => {
            console.error('Data refresh failed:', error);
            return EMPTY;
          }),
        )
        .subscribe();
    }
    this.isRefreshEnabled = isRefreshEnabled;
  }

  async loadData(): Promise<void> {
    if (!this.canLoadData()) {
      return;
    }

    this.isLoading.next(true);
    try {
      await this.loadDataInternal();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      this.isLoading.next(false);
    }
  }

  async reloadData(): Promise<void> {
    this.clear();
    await this.loadData();
  }

  async refreshData(): Promise<void> {
    if (this.canRefreshData()) {
      await this.refreshDataInternal();
    }
  }

  clear(): void {
    this.data = [];
  }

  protected async loadDataInternal(): Promise<void> {
    this.data = await this.dataLoader();
  }

  protected async refreshDataInternal(): Promise<void> {
    await this.loadData();
  }

  protected canLoadData(): boolean {
    return this.isLoading.value === false;
  }

  protected canRefreshData(): boolean {
    return this.isRefreshEnabled;
  }

  private delayFalse(source$: Observable<boolean>, minDelay: number): Observable<boolean> {
    let lastTrueEmissionTime = 0;

    return source$.pipe(
      switchMap(value => {
        if (value || lastTrueEmissionTime === 0) {
          lastTrueEmissionTime = Date.now();
          return of(value);
        } else {
          const timeSinceLastTrue = Date.now() - lastTrueEmissionTime;
          const delayTime = timeSinceLastTrue >= minDelay ? 0 : minDelay - timeSinceLastTrue;
          return of(value).pipe(delay(delayTime));
        }
      }),
    );
  }
}
